import { ethers } from 'ethers6'; // Ethers v6 for our codebase
import { config } from './config';

interface RpcProviderStats {
  url: string;
  responseTime: number;
  lastSuccess: number;
  failureCount: number;
  isHealthy: boolean;
}

class RpcProviderManager {
  private providers: Map<string, ethers.JsonRpcProvider> = new Map();
  private stats: Map<string, RpcProviderStats> = new Map();
  private currentProvider: ethers.JsonRpcProvider | null = null;
  private isInitialized = false;

  constructor() {
    this.initializeProviders();
  }

  private initializeProviders() {
    // Initialize all RPC providers
    // In production: Primary (private Gelato) -> Fallback (public)
    // In localhost development: prefer public first to avoid domain whitelists
    const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    const allRpcUrls = isLocal
      ? [...config.ink.fallbackRpcUrls, config.ink.primaryRpcUrl]
      : [config.ink.primaryRpcUrl, ...config.ink.fallbackRpcUrls];

    allRpcUrls.forEach(url => {
      if (!this.providers.has(url)) {
        const provider = new ethers.JsonRpcProvider(url);
        this.providers.set(url, provider);
        this.stats.set(url, {
          url,
          responseTime: 0,
          lastSuccess: 0,
          failureCount: 0,
          isHealthy: true
        });
      }
    });

    console.log(`🔗 Initialized ${allRpcUrls.length} RPC providers (Primary first: ${!isLocal})`);
  }

  async getBestProvider(_usePrimaryForCritical = false): Promise<ethers.JsonRpcProvider> {
    if (!this.isInitialized) {
      await this.initializeBestProvider();
    }

    // Always use current provider (which prioritizes primary RPC)
    return this.currentProvider!;
  }

  private async initializeBestProvider() {
    try {
      // Always try primary RPC first (your private Gelato Pro)
      const primaryProvider = this.providers.get(config.ink.primaryRpcUrl)!;
      
      try {
        console.log('🔍 Testing primary Gelato Pro RPC...');
        const startTime = Date.now();
        await primaryProvider.getBlockNumber();
        const responseTime = Date.now() - startTime;
        
        this.updateStats(config.ink.primaryRpcUrl, responseTime, true);
        this.currentProvider = primaryProvider;
        this.isInitialized = true;
        
        console.log(`✅ Primary RPC active: Gelato Pro (${responseTime}ms)`);
        return;
      } catch (error) {
        console.error('❌ Gelato Pro RPC failed! This should not happen with your private endpoint.', error);
        this.updateStats(config.ink.primaryRpcUrl, 0, false);
      }

      // If primary fails, try fallback RPCs
      for (const url of config.ink.fallbackRpcUrls) {
        try {
          const provider = this.providers.get(url)!;
          const startTime = Date.now();
          await provider.getBlockNumber();
          const responseTime = Date.now() - startTime;
          
          this.updateStats(url, responseTime, true);
          this.currentProvider = provider;
          this.isInitialized = true;
          
          console.log(`✅ Fallback RPC active: ${url} (${responseTime}ms)`);
          return;
        } catch (error) {
          console.warn(`❌ Fallback RPC failed: ${url}`, error);
          this.updateStats(url, 0, false);
        }
      }

      throw new Error('All RPC endpoints failed to initialize');
    } catch (error) {
      console.error('❌ Failed to initialize RPC providers:', error);
      throw error;
    }
  }

  private updateStats(url: string, responseTime: number, success: boolean) {
    const stats = this.stats.get(url);
    if (stats) {
      stats.responseTime = responseTime;
      stats.lastSuccess = success ? Date.now() : stats.lastSuccess;
      stats.failureCount = success ? 0 : stats.failureCount + 1;
      stats.isHealthy = stats.failureCount < 3; // Mark unhealthy after 3 failures
    }
  }

  async executeWithFailover<T>(
    operation: (provider: ethers.JsonRpcProvider) => Promise<T>,
    _usePrimaryForCritical = false
  ): Promise<T> {
    // Always prioritize primary RPC, then fallback to free public RPCs
    const allRpcUrls = [
      config.ink.primaryRpcUrl,
      ...config.ink.fallbackRpcUrls
    ];

    let lastError: Error | null = null;

    for (const url of allRpcUrls) {
      try {
        const provider = this.providers.get(url);
        if (!provider) continue;

        // Check if this RPC is currently healthy
        const stats = this.stats.get(url);
        if (stats && !stats.isHealthy) {
          console.log(`⏭️ Skipping unhealthy RPC: ${url} (${stats.failureCount} failures)`);
          continue;
        }

        const result = await operation(provider);
        
        // Update stats on success
        this.updateStats(url, 0, true);
        
        return result;
      } catch (error) {
        lastError = error as Error;
        this.updateStats(url, 0, false);
        
        // Log specific error types for better debugging
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        if (errorMessage.includes('429') || errorMessage.includes('Too Many Requests')) {
          console.warn(`⚠️ Rate limited on RPC: ${url} - marking as unhealthy`);
        } else if (errorMessage.includes('CORS') || errorMessage.includes('Failed to fetch')) {
          console.warn(`⚠️ CORS/Fetch error on RPC: ${url} - marking as unhealthy`);
        } else if (errorMessage.includes('block is out of range')) {
          console.warn(`⚠️ Block range error on RPC: ${url}`);
        } else {
          console.warn(`⚠️ RPC failed: ${url}`, errorMessage);
        }
        
        continue;
      }
    }

    throw new Error(`All RPC endpoints failed. Last error: ${lastError?.message}`);
  }

  // Method to get provider stats for debugging
  getStats(): RpcProviderStats[] {
    return Array.from(this.stats.values());
  }

  // Method to force re-initialization (useful for recovery)
  async reinitialize() {
    this.isInitialized = false;
    this.currentProvider = null;
    await this.initializeBestProvider();
  }
}

// Singleton instance
let rpcManager: RpcProviderManager | null = null;

export const getRpcManager = (): RpcProviderManager => {
  if (!rpcManager) {
    rpcManager = new RpcProviderManager();
  }
  return rpcManager;
};

// Convenience functions
export const getBestProvider = async (usePrimaryForCritical = false): Promise<ethers.JsonRpcProvider> => {
  return getRpcManager().getBestProvider(usePrimaryForCritical);
};

export const executeWithRpcFailover = async <T>(
  operation: (provider: ethers.JsonRpcProvider) => Promise<T>,
  usePrimaryForCritical = false
): Promise<T> => {
  return getRpcManager().executeWithFailover(operation, usePrimaryForCritical);
};

export const getRpcStats = (): RpcProviderStats[] => {
  return getRpcManager().getStats();
};
